/**
 * Module tiles — one card per CRM module (Lead Accelerator, Lost leads,
 * Triage & QA, Customers, Deals). Every card reads the same way: the headline
 * number on the left, a segmented ring on the right, and a legend underneath
 * whose rows are the ring's segments — so the cards line up and compare at a
 * glance. Replaces the old dashboard's module row.
 */
import { Link } from 'react-router-dom';
import { fmtInt } from './format';
import './modern.css';

/**
 * value/label: headline figure.  meta: one supporting line.
 * segments: [{ label, value, color }] — drawn in the ring and listed below.
 * ring: { value, label } — the figure in the ring's centre (e.g. "84%", "fresh").
 */
export function ModuleCard({ title, icon, tone, to, value, label, meta, segments = [], ring, index = 0 }) {
  return (
    <Link to={to} className="md-mod md-rise" style={{ '--tone': tone, animationDelay: `${index * 50}ms` }}>
      <div className="md-mod-head">
        <span className="md-mod-icon">{icon}</span>
        <span className="md-mod-title">{title}</span>
        <span className="md-mod-go" aria-hidden>→</span>
      </div>

      <div className="md-mod-main">
        <div className="md-mod-num">
          <b>{value}</b>
          <span>{label}</span>
          {meta && <small>{meta}</small>}
        </div>
        <Ring segments={segments} center={ring} />
      </div>

      <ul className="md-mod-legend">
        {segments.map((s) => (
          <li key={s.label}>
            <em style={{ background: s.color }} />
            <span>{s.label}</span>
            <b>{fmtInt(s.value || 0)}</b>
          </li>
        ))}
      </ul>
    </Link>
  );
}

function Ring({ segments = [], center, size = 70, stroke = 8 }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const gap = total && segments.filter((s) => s.value).length > 1 ? 2.5 : 0;
  let offset = 0;
  return (
    <div className="md-mod-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border-color)" strokeWidth={stroke} />
        {total > 0 && segments.filter((s) => s.value).map((s) => {
          const len = (s.value / total) * c;
          const el = (
            <circle key={s.label} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={stroke}
              strokeDasharray={`${Math.max(0, len - gap)} ${c}`} strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`} strokeLinecap="butt" />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="md-mod-ring-c">
        <b>{total ? center?.value : '—'}</b>
        {total > 0 && center?.label && <small>{center.label}</small>}
      </div>
    </div>
  );
}
